import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSourcingCompanySummary } from '../../../services/queries/SourcingCompanyQueries';
import { resolveCompanyLookup } from './resolveCompanyLookup';

export const SOURCING_COMPANY_SUMMARY_QUERY_KEY = 'sourcing-company-summary';

// Summaries are LLM-generated and expensive to refresh; the backend caches
// per-company on its side. Holding the result for an hour on the client is a
// safe upper bound that still lets users see refreshed bullets when they
// navigate back to the page on a new session.
const ONE_HOUR = 60 * 60 * 1000;

interface UseSourcingCompanySummaryResult {
  readonly summary: readonly string[];
  readonly generatedAt: string | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Fetches the Foresight AI narrative summary for the sourcing profile
 * page. Mirrors `useSourcingCompanyNews` so the panel loads independently
 * of the merged profile (and degrades gracefully — empty `summary`
 * → panel hides itself).
 */
export function useSourcingCompanySummary(companyId: string): UseSourcingCompanySummaryResult {
  const lookup = useMemo(() => resolveCompanyLookup(companyId), [companyId]);
  const hasLookup = Object.keys(lookup).length > 0;

  const { data, isLoading, isError } = useQuery({
    queryKey: [SOURCING_COMPANY_SUMMARY_QUERY_KEY, companyId],
    queryFn: async ({ signal }) => fetchSourcingCompanySummary(lookup, signal),
    staleTime: ONE_HOUR,
    enabled: Boolean(companyId) && hasLookup,
    // The agents call may be slow — don't aggressively retry on failure;
    // an empty list is the contractual "no summary" signal handled by the UI.
    retry: 1,
  });

  return {
    summary: data?.summary ?? [],
    generatedAt: data?.generatedAt ?? null,
    isLoading,
    isError,
  };
}
